import {PatientExtended} from '@/features/patients/types'
import {useTranslation} from '@/translations/i18n'
import {useNavigate} from 'react-router-dom'
import {AgendaEventAppointment} from '../../types'
import {MedicalReportType} from '@/types'
import {toast} from '@/utilities/toast/toast'
import {generateCreateMedicalReportPath} from '@/features/medical-reports/utils'
import {generateMedicalRecord} from '@/features/medical-records/utils'
import {useMemo} from 'react'
import {useGetInfiniteMedicalReportsByPatient} from '@/features/medical-reports/queries/useGetInfiniteMedicalReportsByPatient'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Button} from '@/components/ui/button/Button'
import {File02Icon, PlusIcon} from '@/components/ui/icon/Icon'

export const AgendaEventDetailsNavigateToMedicalRecord = ({
    patient,
    agendaEvent
}: {
    patient: PatientExtended
    agendaEvent: AgendaEventAppointment
}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()

    const {data: reports, isLoading: isLoadingReport} = useGetInfiniteMedicalReportsByPatient({
        urlParams: {patientId: patient.id},
        params: {
            projectId: agendaEvent.eventPatients?.[0]?.projectId ?? undefined,
            medicalRecordReportTypeId:
                agendaEvent.eventConsultants?.[0]?.accessType?.medicalRecordReportType?.id ?? undefined,
            limit: 1
        },
        options: {
            enabled:
                !!agendaEvent.eventPatients?.[0]?.projectId &&
                !!agendaEvent.eventConsultants?.[0]?.accessType?.medicalRecordReportType
        }
    })

    const handleNavigateToCreateMedicalReport = (
        patientId: string,
        projectId?: string | null,
        medicalReportType?: MedicalReportType | null,
        reportDate?: Date | null
    ) => {
        const path = generateCreateMedicalReportPath(patientId, projectId, medicalReportType, null, reportDate)

        if (!path) {
            toast.error(t('agenda:error_navigate_to_medical_record'))
            return
        }

        navigate(path)
    }

    const handleNavigateToMedicalRecord = (
        patientId: string,
        projectId?: string | null,
        medicalReportType?: MedicalReportType | null
    ) => {
        const path = generateMedicalRecord(patientId, projectId, medicalReportType)

        if (!path) {
            toast.error(t('agenda:error_navigate_to_medical_record'))
            return
        }

        navigate(path)
    }

    const isReportAvailable = useMemo(() => !!(reports && reports?.pages[0]?.data?.length > 0), [reports])

    if (isLoadingReport) {
        return <Skeleton width={100} height={28} />
    }

    if (isReportAvailable) {
        return (
            <Button
                onClick={() => {
                    handleNavigateToMedicalRecord(
                        patient.id,
                        agendaEvent.eventPatients?.[0]?.projectId,
                        agendaEvent.eventConsultants?.[0]?.accessType?.medicalRecordReportType
                    )
                }}
                variant="secondary"
                size="sm"
            >
                <File02Icon />
                {t('agenda:show_report_in_medical_record')}
            </Button>
        )
    }

    return (
        <Button
            onClick={() =>
                handleNavigateToCreateMedicalReport(
                    patient.id,
                    agendaEvent.eventPatients?.[0]?.projectId,
                    agendaEvent.eventConsultants?.[0]?.accessType?.medicalRecordReportType,
                    agendaEvent.date ? new Date(agendaEvent.date) : null
                )
            }
            variant="secondary"
            size="sm"
        >
            <PlusIcon />
            {t('commons:report', {count: 1})}
        </Button>
    )
}
